import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {Box, Card, Grid, LogoSuite, Pillar, Section, SectionIntroStacked, Statistic} from '@primer/react-brand'
import type {ReactElement} from 'react'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'
import {Logo} from '../../../components/FlexSuiteLogos/Logo'
import {renderWithNoMarks} from '../../../utils'
import type {IntroSectionProps} from './types'
import styles from './IntroSection.module.css'
import overviewStyles from '../../OverviewTemplate.module.css'

type IntroContextContentProps = Pick<
  IntroSectionProps,
  | 'introContext'
  | 'introContextHeading'
  | 'introContextLink'
  | 'introContextBody'
  | 'introItems'
  | 'introIconItems'
  | 'introImageCards'
  | 'introStats'
>

export function IntroSection({
  introLogoHeading,
  introLogos,
  introLogoLink,
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconItems,
  introImageCards,
  introStats,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading = true,
}: IntroSectionProps) {
  const introContextContent = renderIntroContextContent({
    introContext,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconItems,
    introImageCards,
    introStats,
  })

  const hasIntroContent = Boolean(introLogos?.length) || introContextContent !== null

  if (!hasIntroContent) return null

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      {introLogos && introLogos.length > 0 ? (
        <LogoSuite align="center" variant={logoSuiteOptions?.variant}>
          {introLogoHeading ? (
            <LogoSuite.Heading visuallyHidden={hideLogoHeading}>{introLogoHeading}</LogoSuite.Heading>
          ) : null}
          <LogoSuite.Logobar
            variant={logobarOptions?.variant}
            marquee={logobarOptions?.marquee}
            marqueeSpeed={logobarOptions?.marqueeSpeed}
            takeoverButton={takeoverButton}
          >
            {introLogos.map(logo => (
              <Logo key={logo} name={logo} />
            ))}
          </LogoSuite.Logobar>
        </LogoSuite>
      ) : null}
      {introContextContent}
    </Section>
  )
}

function renderIntroContextContent({
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconItems,
  introImageCards,
  introStats,
}: IntroContextContentProps): ReactElement | null {
  if (!introContext) return null

  switch (introContext) {
    case 'Simple Intro':
      if (!introContextHeading && !introContextBody) return null
      return (
        <Box marginBlockStart={{narrow: 48, regular: 80}} marginBlockEnd={{narrow: 48, regular: 80}}>
          <Grid>
            <Grid.Column>
              <SectionIntroStacked>
                {introContextHeading ? (
                  <SectionIntroStacked.Heading>{renderWithNoMarks(introContextHeading)}</SectionIntroStacked.Heading>
                ) : null}
                {introContextBody ? (
                  <SectionIntroStacked.Description>
                    {renderWithNoMarks(introContextBody)}
                  </SectionIntroStacked.Description>
                ) : null}
                {introContextLink?.fields.href && (
                  <SectionIntroStacked.Link href={introContextLink.fields.href}>
                    {introContextLink.fields.text}
                  </SectionIntroStacked.Link>
                )}
                {introItems && introItems.length > 0 ? (
                  <SectionIntroStacked.Items>
                    {introItems.map(item => {
                      const isIconItem = item.sys.contentType.sys.id === 'iconLinkItem'
                      const IconComponent = isIconItem && 'icon' in item.fields ? getPrimerIcon(item.fields.icon) : null
                      return (
                        <SectionIntroStacked.Item key={item.sys.id}>
                          {IconComponent ? <SectionIntroStacked.ItemIcon icon={<IconComponent />} /> : null}
                          <SectionIntroStacked.ItemHeading>
                            {renderWithNoMarks(item.fields.heading)}
                          </SectionIntroStacked.ItemHeading>
                          <SectionIntroStacked.ItemDescription>
                            {renderWithNoMarks(item.fields.description)}
                          </SectionIntroStacked.ItemDescription>
                        </SectionIntroStacked.Item>
                      )
                    })}
                  </SectionIntroStacked.Items>
                ) : null}
              </SectionIntroStacked>
            </Grid.Column>
          </Grid>
        </Box>
      )
    case 'Pillars':
      if (!introIconItems || introIconItems.length === 0) return null
      return (
        <Box className={styles.cardGridFrame}>
          <Grid columnGap="none" className={overviewStyles.gridContent}>
            {introIconItems.map(item => {
              const IconComponent = getPrimerIcon(item.fields.icon)
              return (
                <Grid.Column key={item.sys.id} span={{medium: 4}} className="gridColumn">
                  <Box
                    className={styles.gridItem}
                    padding={{narrow: 24, regular: 40}}
                    paddingBlockStart={{regular: 80}}
                    paddingBlockEnd={{regular: 80}}
                    borderWidth="thin"
                    borderColor="muted"
                  >
                    <Pillar>
                      {IconComponent ? <Pillar.Icon icon={<IconComponent />} /> : null}
                      <Pillar.Heading>{renderWithNoMarks(item.fields.heading)}</Pillar.Heading>
                      <Pillar.Description>{renderWithNoMarks(item.fields.description)}</Pillar.Description>
                    </Pillar>
                  </Box>
                </Grid.Column>
              )
            })}
          </Grid>
        </Box>
      )
    case 'Icon Cards':
      if (!introIconItems || introIconItems.length === 0) return null
      return (
        <Box className={styles.cardGridFrame}>
          <Grid className={overviewStyles.gridContent}>
            {introIconItems.map(item => {
              const IconComponent = getPrimerIcon(item.fields.icon)
              return (
                <Grid.Column key={item.sys.id} span={{medium: 4}} className="gridColumn">
                  <Box
                    className={styles.gridItem}
                    padding={{narrow: 24, regular: 40}}
                    paddingBlockStart={{regular: 80}}
                    paddingBlockEnd={{regular: 80}}
                    borderWidth="thin"
                    borderColor="muted"
                  >
                    <Card href={item.fields.href} variant="minimal" fullWidth ctaText={item.fields.label}>
                      {IconComponent ? <Card.Icon icon={<IconComponent />} color="default" hasBackground /> : null}
                      <Card.Heading>{renderWithNoMarks(item.fields.heading)}</Card.Heading>
                      <Card.Description>{renderWithNoMarks(item.fields.description)}</Card.Description>
                    </Card>
                  </Box>
                </Grid.Column>
              )
            })}
          </Grid>
        </Box>
      )
    case 'Image Cards':
      if (!introImageCards || introImageCards.length === 0) return null
      return (
        <Box marginBlockStart={48}>
          <Grid>
            {introImageCards.map(card => (
              <Grid.Column key={card.sys.id} span={{xsmall: 12, medium: 6, large: 4}}>
                <Card href={card.fields.href} variant="minimal" fullWidth ctaText={card.fields.label}>
                  <Card.Image
                    src={`${card.fields.image.fields.file.url}?fm=webp`}
                    alt={card.fields.image.fields.description || documentToPlainTextString(card.fields.heading) || ''}
                    sources={getImageSources(card.fields.image.fields.file.url, {
                      maxWidth: MAX_CONTENT_WIDTH / 3,
                    })}
                  />
                  <Card.Heading>{renderWithNoMarks(card.fields.heading)}</Card.Heading>
                  <Card.Description>{renderWithNoMarks(card.fields.description)}</Card.Description>
                </Card>
              </Grid.Column>
            ))}
          </Grid>
        </Box>
      )
    case 'Stats':
      if (!introStats || introStats.length === 0) return null
      return (
        <Box marginBlockStart={{narrow: 48, regular: 80}} marginBlockEnd={{narrow: 48, regular: 80}}>
          <Grid>
            {introStats.map(stat => (
              <Grid.Column key={stat.sys.id} span={{medium: 4}}>
                <Statistic>
                  <Statistic.Heading>{renderWithNoMarks(stat.fields.heading)}</Statistic.Heading>
                  <Statistic.Description>{renderWithNoMarks(stat.fields.description)}</Statistic.Description>
                </Statistic>
              </Grid.Column>
            ))}
          </Grid>
        </Box>
      )
    default:
      return null
  }
}
